import api, { apiUpload, uploadWithRetry } from "./api";

export interface User {
  id: number;
  student_id?: string;
  registration_code?: string;
  email: string;
  name: string;
  role: "student" | "admin";
  status: "pending" | "approved" | "blocked";
  blocked_until: string | null;
  created_at: string;
  updated_at: string;
  last_login?: string;
  phone?: string;
  age?: number;
  date_of_birth?: string;
  village?: string;
  district?: string;
  province?: string;
  profile_image?: string;
}

export interface Video {
  id: number;
  title: string;
  description: string;
  category: string;
  folder_id?: number;
  folder_name?: string;
  duration: number;
  cloudinary_public_id: string;
  cloudinary_url: string;
  thumbnail_url: string | null;
  thumbnail_color?: string;
  views_count: number;
  created_at: string;
  updated_at: string;
  views?: number;
  completion_rate?: number;
  sort_order?: number;
  status: "processing" | "ready" | "published" | "archived";
}

export interface Folder {
  id: number;
  name: string;
  description?: string;
  parent_id?: number | null;
  created_by_name?: string;
  video_count: number;
  subfolder_count: number;
  created_at: string;
  updated_at: string;
  sort_order?: number;
  children?: Folder[];
  videos?: Video[];
  subfolders?: Folder[];
}

export interface Category {
  id: number;
  name: string;
  description?: string;
  color: string;
  order: number;
  video_count: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ActivityLog {
  id: number;
  user_id: number;
  user_email: string;
  action: string;
  details: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

export interface Statistics {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  blockedUsers: number;
  totalVideos: number;
  totalViews: number;
  completedCourses: number;
  averageProgress: number;
}

export interface Analytics {
  userGrowth: Array<{ date: string; count: number }>;
  videoViews: Array<{ date: string; views: number }>;
  categoryProgress: Array<{
    category: string;
    progress: number;
    users: number;
  }>;
  topVideos: Array<{
    id: number;
    title: string;
    views: number;
    completion_rate: number;
  }>;
  userEngagement: {
    daily_active_users: number;
    weekly_active_users: number;
    monthly_active_users: number;
    average_watch_time: number;
  };
}

interface UserCreateData {
  name: string;
  email: string;
  password: string;
  role: "student" | "admin";
  registration_code?: string;
}

export const adminService = {
  // Users
  getUser: async (userId: number) => {
    console.log("adminService.getUser called with userId:", userId);
    try {
      const response = await api.get(`/admin/users/${userId}`);
      console.log("adminService.getUser response:", response.data);
      return response.data;
    } catch (error) {
      console.error("adminService.getUser error:", error);
      throw error;
    }
  },

  getUsers: async (page = 1, limit = 10, status = "", search = "") => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status) params.append("status", status);
    if (search) params.append("search", search);

    const response = await api.get(`/admin/users?${params}`);
    return response.data;
  },

  createUser: async (userData: UserCreateData) => {
    try {
      console.log("Creating user with data:", userData);
      const response = await api.post("/admin/users", userData);
      console.log("User creation response:", response.data);
      return response.data;
    } catch (error) {
      console.error("User creation error:", error);
      throw error;
    }
  },

  approveUser: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/approve`);
    return response.data;
  },

  blockUser: async (userId: number, reason?: string) => {
    const data = reason ? { reason } : {};
    const response = await api.post(`/admin/users/${userId}/block`, data);
    return response.data;
  },

  unblockUser: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/unblock`);
    return response.data;
  },

  deleteUser: async (userId: number) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  updateUser: async (
    userId: number,
    userData: {
      name?: string;
      email?: string;
      role?: "student" | "admin";
      status?: "pending" | "approved" | "blocked";
      phone?: string;
      age?: number;
      date_of_birth?: string;
      village?: string;
      district?: string;
      province?: string;
      registration_code?: string;
    },
  ) => {
    const response = await api.put(`/admin/users/${userId}`, userData);
    return response.data;
  },

  // Videos
  // Videos
  getVideos: async (page = 1, limit = 10, search = "", root = false) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) params.append("search", search);
    if (root) params.append("root", "true");

    const response = await api.get(`/videos?${params}`);
    return response.data;
  },

  uploadVideo: async (formData: FormData) => {
    console.log("🚀 Starting optimized video upload...");
    try {
      // Try fast upload first (most uploads succeed on first try)
      const { fastUpload } = await import("./api");
      const response = await fastUpload("/videos", formData);
      return response.data;
    } catch (error: any) {
      // Only use retry mechanism if it's a network error
      const isNetworkError =
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.code === "ENOTFOUND";
      if (isNetworkError) {
        console.log(
          "Network error detected, falling back to retry mechanism...",
        );
        const response = await uploadWithRetry("/videos", formData);
        return response.data;
      }
      // Re-throw server errors immediately (don't retry)
      throw error;
    }
  },

  updateVideo: async (videoId: number, formData: FormData) => {
    try {
      // Check if there are any files in the FormData
      let hasFiles = false;
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          hasFiles = true;
          break;
        }
      }

      if (hasFiles) {
        // If there are files, send FormData directly
        const response = await api.put(`/videos/${videoId}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        return response.data;
      } else {
        // For metadata-only updates, convert to JSON
        const updateData: any = {};
        for (let [key, value] of formData.entries()) {
          updateData[key] = value;
        }

        const response = await api.put(`/videos/${videoId}`, updateData);
        return response.data;
      }
    } catch (error: any) {
      // Don't retry for authentication errors or server errors
      if (
        error.response &&
        (error.response.status === 401 ||
          error.response.status === 403 ||
          error.response.status === 404)
      ) {
        throw error;
      }

      // Only retry for network errors
      const isNetworkError =
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.code === "ENOTFOUND";
      if (isNetworkError) {
        const response = await uploadWithRetry(`/videos/${videoId}`, formData);
        return response.data;
      }

      // Re-throw server errors immediately (don't retry)
      throw error;
    }
  },

  updateVideoMetadata: async (videoId: number, updateData: any) => {
    try {
      // Fast metadata-only update using JSON
      const response = await api.put(`/videos/${videoId}/metadata`, updateData);
      return response.data;
    } catch (error: any) {
      console.error("Error updating video metadata:", error);
      throw error;
    }
  },

  deleteVideo: async (videoId: number) => {
    const response = await api.delete(`/videos/${videoId}`);
    return response.data;
  },

  // Folders
  getFolders: async () => {
    const response = await api.get("/folders");
    return response.data;
  },
  getFolder: async (id: number) => {
    const response = await api.get(`/folders/${id}`);
    return response.data;
  },
  createFolder: async (data: {
    name: string;
    description?: string;
    parent_id?: number;
    sort_order?: number;
  }) => {
    const response = await api.post("/folders", data);
    return response.data;
  },
  updateFolder: async (
    id: number,
    data: {
      name?: string;
      description?: string;
      parent_id?: number | null;
      sort_order?: number;
    },
  ) => {
    const response = await api.put(`/folders/${id}`, data);
    return response.data;
  },
  deleteFolder: async (id: number) => {
    const response = await api.delete(`/folders/${id}`);
    return response.data;
  },
  moveVideosToFolder: async (folder_id: number, video_ids: number[]) => {
    const response = await api.post(`/folders/${folder_id}/move-videos`, {
      video_ids,
    });
    return response.data;
  },
  reorderFiles: async (
    file_orders: Array<{ id: number; sort_order: number }>,
  ) => {
    const response = await api.post("/videos/reorder", { file_orders });
    return response.data;
  },

  // Categories
  getCategories: async () => {
    const response = await api.get("/categories");
    return response.data;
  },

  getCategory: async (categoryId: number) => {
    const response = await api.get(`/categories/${categoryId}`);
    return response.data;
  },

  createCategory: async (categoryData: {
    name: string;
    description?: string;
    color?: string;
    order?: number;
  }) => {
    const response = await api.post("/categories", categoryData);
    return response.data;
  },

  updateCategory: async (
    categoryId: number,
    categoryData: {
      name: string;
      description?: string;
      color?: string;
      order?: number;
    },
  ) => {
    const response = await api.put(`/categories/${categoryId}`, categoryData);
    return response.data;
  },

  deleteCategory: async (categoryId: number) => {
    const response = await api.delete(`/categories/${categoryId}`);
    console.log("🚀 ~ response:", response);
    return response.data;
  },

  // Analytics
  getDashboardStats: async () => {
    const response = await api.get("/analytics/dashboard");
    return response.data;
  },

  getAnalytics: async (range: string = "30d") => {
    try {
      const response = await api.get(`/analytics/data?range=${range}`);
      return response.data;
    } catch (error) {
      console.error("Analytics API error:", error);
      throw error;
    }
  },

  // Activity Logs
  getActivityLogs: async (
    page = 1,
    limit = 20,
    userId = "",
    action = "",
    search = "",
    startDate = "",
    endDate = "",
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (userId) params.append("userId", userId);
    if (action) params.append("action", action);
    if (search) params.append("search", search);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const response = await api.get(`/admin/activity-logs?${params}`);
    return response.data;
  },

  // Conversations
  deleteConversation: async (userId: number) => {
    const response = await api.delete(`/messages/conversations/${userId}`);
    return response.data;
  },

  exportData: async (
    type: "users" | "videos" | "activity",
    format: "csv" | "json" = "json",
  ) => {
    const response = await api.get(
      `/analytics/export?type=${type}&format=${format}`,
    );
    return response.data;
  },

  getProfile: async () => {
    try {
      console.log("Fetching admin profile data from server...");
      const response = await api.get("/admin/profile");
      console.log("Admin profile data received from server:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching admin profile data:", error);
      throw error;
    }
  },

  updateProfile: async (data: {
    name: string;
    currentPassword?: string;
    newPassword?: string;
    phone?: string;
    age?: number;
    date_of_birth?: string;
    village?: string;
    district?: string;
    province?: string;
  }) => {
    try {
      console.log("Updating admin profile with data:", data);
      const response = await api.put("/admin/profile", data);
      console.log("Admin profile update response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating admin profile:", error);
      throw error;
    }
  },

  updateProfileWithImage: async (formData: FormData) => {
    try {
      console.log("Updating admin profile with image...");
      // Log FormData entries
      for (let [key, value] of formData.entries()) {
        console.log(
          key,
          typeof value === "object" ? `File: ${(value as File).name}` : value,
        );
      }

      const response = await api.put("/admin/profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      console.log("Admin profile update with image response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating admin profile with image:", error);
      throw error;
    }
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }) => {
    try {
      console.log("Changing admin password...");
      const response = await api.post("/admin/change-password", data);
      console.log("Admin password change response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error changing admin password:", error);
      throw error;
    }
  },
};
